import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import { toast } from "sonner";
import { ArrowLeft, Check, IndianRupee, ShieldCheck } from "lucide-react";

export default function Donate() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useT();
  const [school, setSchool] = useState(null);
  const [settings, setSettings] = useState({ price_per_set: 500, minimum_donation_sets: 20 });
  const [qty, setQty] = useState(20);
  const [anonymous, setAnonymous] = useState(false);
  const [dedication, setDedication] = useState("");
  const [monthly, setMonthly] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    api.get(`/public/schools/${schoolId}`).then(r => setSchool(r.data)).catch(() => {});
    api.get("/settings").then(r => setSettings(r.data)).catch(() => {});
  }, [schoolId]);

  useEffect(() => {
    if (settings?.minimum_donation_sets && qty < settings.minimum_donation_sets) setQty(settings.minimum_donation_sets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.minimum_donation_sets]);

  const total = useMemo(() => (settings.price_per_set || 500) * qty, [settings, qty]);

  const handleDonate = async () => {
    if (!user) {
      toast.error("Please sign in to sponsor.");
      navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    if (qty < settings.minimum_donation_sets) {
      toast.error(`Minimum sponsorship is ${settings.minimum_donation_sets} sets.`);
      return;
    }
    setProcessing(true);
    try {
      const basePayload = {
        school_id: schoolId,
        quantity: qty,
        anonymous,
        dedication: dedication || null,
        donor_name: user.name,
        donor_email: user.email,
      };

      if (monthly) {
        const { data: pledge } = await api.post("/pledges/create", { ...basePayload, frequency: "monthly" });
        setSuccess({ pledge: true, pledge_id: pledge.pledge_id, amount: pledge.amount_per_charge, next_charge_at: pledge.next_charge_at });
        toast.success("Monthly pledge created. Thank you!");
        return;
      }

      const { data: order } = await api.post("/donations/create-order", basePayload);

      const payload = await openRazorpay(order, user.name, user.email);

      const { data: verified } = await api.post("/donations/verify", {
        donation_id: order.donation_id,
        razorpay_order_id: payload.razorpay_order_id,
        razorpay_payment_id: payload.razorpay_payment_id,
        razorpay_signature: payload.razorpay_signature,
      });

      if (verified.payment_status === "SUCCESS") {
        setSuccess({ donation_id: verified.donation_id });
        toast.success("Sponsorship confirmed. Thank you!");
      } else {
        toast.error("Payment not verified.");
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setProcessing(false);
    }
  };

  if (!school) return <PublicLayout><div className="p-20 text-center">Loading…</div></PublicLayout>;

  if (success) {
    return (
      <PublicLayout>
        <section className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="donation-success">
          <div className="w-16 h-16 rounded-full bg-[var(--bb-success)]/10 text-[var(--bb-success)] grid place-items-center mx-auto"><Check size={28}/></div>
          {success.pledge ? (
            <>
              <h1 className="font-serif text-5xl md:text-6xl text-[var(--bb-forest)] mt-8 tracking-tight">A monthly promise.</h1>
              <p className="mt-6 text-[var(--bb-ink)]/75 text-lg">Your pledge of <b>₹{success.amount?.toLocaleString()}/month</b> for <b>{school.name}</b> is active. First scheduled charge: <b>{success.next_charge_at?.slice(0,10)}</b>.</p>
              <p className="mt-3 text-sm text-[var(--bb-muted)]">You can pause or cancel anytime from your donor dashboard.</p>
              <div className="mt-10 flex justify-center gap-4 flex-wrap">
                <Link to="/donor" className="bb-btn-primary">Go to donor dashboard</Link>
                <Link to="/schools" className="bb-btn-secondary">Browse more schools</Link>
              </div>
            </>
          ) : (
            <>
              <h1 className="font-serif text-5xl md:text-6xl text-[var(--bb-forest)] mt-8 tracking-tight">{t("success_title")}</h1>
              <p className="mt-6 text-[var(--bb-ink)]/75 text-lg">{t("success_body")}</p>
              <div className="mt-3 text-xs text-[var(--bb-muted)]">{t("success_receipt_note")}</div>
              <div className="mt-10 flex justify-center gap-4 flex-wrap">
                <Link to="/donor" className="bb-btn-primary">Go to dashboard</Link>
                <Link to="/schools" className="bb-btn-secondary inline-flex items-center gap-2">Sponsor more schools</Link>
              </div>
            </>
          )}
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="max-w-5xl mx-auto px-6 md:px-10 pt-10 pb-24">
        <Link to={`/schools/${schoolId}`} className="bb-link text-sm inline-flex items-center gap-2 mb-8"><ArrowLeft size={14}/> Back to school</Link>

        <div className="grid md:grid-cols-5 gap-10">
          <div className="md:col-span-3">
            <div className="bb-overline text-[var(--bb-terracotta)] mb-3">YOU ARE SPONSORING</div>
            <h1 className="font-serif text-4xl md:text-5xl text-[var(--bb-forest)] tracking-tight leading-tight">{school.name}</h1>
            <div className="text-sm text-[var(--bb-muted)] mt-2">{school.address}</div>

            <div className="mt-10 bb-card p-8 space-y-6">
              <div>
                <label className="bb-overline text-[var(--bb-muted)]">Number of sets</label>
                <div className="flex items-center gap-3 mt-3">
                  <button onClick={()=>setQty(Math.max(settings.minimum_donation_sets, qty-1))} className="w-10 h-10 rounded-full border border-[var(--bb-border)] hover:bg-[var(--bb-sand)]" data-testid="qty-dec">−</button>
                  <input
                    type="number"
                    value={qty}
                    min={settings.minimum_donation_sets}
                    onChange={(e)=>setQty(parseInt(e.target.value||"0",10))}
                    className="flex-1 text-center font-serif text-3xl bg-transparent border-b border-[var(--bb-border)] focus:border-[var(--bb-terracotta)] outline-none py-2"
                    data-testid="qty-input"
                  />
                  <button onClick={()=>setQty(qty+1)} className="w-10 h-10 rounded-full border border-[var(--bb-border)] hover:bg-[var(--bb-sand)]" data-testid="qty-inc">+</button>
                </div>
                <div className="mt-2 text-xs text-[var(--bb-muted)]">Minimum {settings.minimum_donation_sets} sets · ₹{settings.price_per_set} per set</div>
                <div className="mt-3 flex gap-2 flex-wrap">
                  {[settings.minimum_donation_sets, 50, 100, 200].map(n => (
                    <button key={n} onClick={()=>setQty(n)} className="text-xs px-3 py-1.5 rounded-full border border-[var(--bb-border)] hover:border-[var(--bb-terracotta)] hover:text-[var(--bb-terracotta)]" data-testid={`preset-${n}`}>{n} sets</button>
                  ))}
                </div>
              </div>

              {!user && (
                <div className="bb-card p-5 bg-[var(--bb-terracotta)]/5 border-[var(--bb-terracotta)]/30">
                  <div className="bb-overline text-[var(--bb-terracotta)] mb-2">SIGN IN REQUIRED</div>
                  <p className="text-sm text-[var(--bb-ink)]/85 mb-3">Please sign in with Google to record your sponsorship in your account and receive the email confirmation.</p>
                  <Link to={`/login?next=${encodeURIComponent(window.location.pathname)}`} className="bb-btn-primary text-sm inline-flex items-center gap-2" data-testid="donate-signin">Sign in with Google</Link>
                </div>
              )}

              <div>
                <label className="bb-overline text-[var(--bb-muted)]">Dedication (optional)</label>
                <input className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)]" placeholder="In memory of…" value={dedication} onChange={e=>setDedication(e.target.value)} data-testid="dedication"/>
              </div>

              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="anonymous-toggle-wrap">
                <input type="checkbox" checked={anonymous} onChange={(e)=>setAnonymous(e.target.checked)} data-testid="anonymous-toggle"/>
                Sponsor anonymously (your name will not be shown publicly)
              </label>

              <label className="flex items-start gap-2 text-sm cursor-pointer border-t border-[var(--bb-border)] pt-5" data-testid="monthly-toggle-wrap">
                <input type="checkbox" checked={monthly} onChange={(e)=>setMonthly(e.target.checked)} className="mt-1" data-testid="monthly-toggle"/>
                <div>
                  <div className="font-medium text-[var(--bb-forest)]">{t("donate_monthly")}</div>
                  <div className="text-xs text-[var(--bb-muted)] mt-0.5">{t("donate_monthly_hint")}</div>
                </div>
              </label>
            </div>
          </div>

          <aside className="md:col-span-2">
            <div className="bb-card p-8 sticky top-28">
              <div className="bb-overline text-[var(--bb-muted)] mb-2">SUMMARY</div>
              <div className="flex items-baseline justify-between mb-6">
                <div className="font-serif text-5xl text-[var(--bb-forest)] flex items-center"><IndianRupee size={32} strokeWidth={2}/>{total.toLocaleString()}</div>
              </div>
              <div className="text-sm space-y-2 text-[var(--bb-ink)]/80">
                <div className="flex justify-between"><span>{qty} × ₹{settings.price_per_set}</span><span>₹{total.toLocaleString()}</span></div>
                <div className="flex justify-between text-xs text-[var(--bb-muted)]"><span>Books included</span><span>{qty * 12} comics</span></div>
                <div className="flex justify-between text-xs text-[var(--bb-muted)]"><span>Students reached</span><span>~{qty}</span></div>
              </div>
              <button onClick={handleDonate} disabled={processing} className="bb-btn-primary w-full mt-8" data-testid="donate-pay-btn">
                {processing ? "Processing…" : (monthly ? `Sponsor ₹${total.toLocaleString()}/month` : "Sponsor Books")}
              </button>

              <div className="mt-3 text-[11px] text-center text-[var(--bb-muted)] leading-relaxed" data-testid="sponsor-purchase-note">
                {t("sponsor_purchase_note")}
              </div>

              <div className="mt-5 space-y-3 text-xs text-[var(--bb-muted)]">
                <div className="flex items-start gap-2" data-testid="donate-secure-note">
                  <ShieldCheck size={14} className="text-[var(--bb-forest)] shrink-0 mt-0.5"/>
                  <span>{t("payment_trust_line")}</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </PublicLayout>
  );
}

function openRazorpay(order, name, email) {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => {
      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Bala Basava Movement",
        description: "Purchase of Bala Basava educational comic sets for distribution",
        handler: (resp) => resolve(resp),
        prefill: { name, email },
        theme: { color: "#C25946" },
        modal: { ondismiss: () => reject(new Error("Checkout closed")) },
      });
      rzp.open();
    };
    script.onerror = () => reject(new Error("Razorpay SDK load failed"));
    document.body.appendChild(script);
  });
}
